/**
 * TNK-154 — operator workbench theme application.
 *
 * Reads `appPreferences.theme` (light / dark / system; default system)
 * and pins `<html data-theme="light|dark">` so the CSS variable buckets
 * in `index.css` resolve correctly. System mode subscribes to
 * `prefers-color-scheme` so a daylight-vs-night OS flip retunes the UI
 * without a page reload.
 *
 * Storefront (`/shop/*`) is scoped under `.shop-theme` and stays on its
 * own dark+gold palette regardless of the operator's pick — see
 * `styles/shop.theme.css`.
 *
 * Pair with the inline first-paint script in `index.html` (TNK-154
 * phase C) — that script sets `data-theme` from localStorage before
 * React mounts, so a hard refresh in light mode doesn't briefly flash
 * the dark default. This hook is the authoritative source after mount
 * and overrides whatever the inline script picked.
 */
import { useEffect } from "react";

import type { Theme } from "@/context/AppContext";

/** Concrete `data-theme` values — the `Theme` union minus `system`,
 *  which is resolved at runtime to `light` or `dark` based on the OS. */
type ResolvedTheme = Exclude<Theme, "system">;

function resolveSystem(): "light" | "dark" {
  if (
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function"
  ) {
    return "dark";
  }
  return window.matchMedia("(prefers-color-scheme: light)").matches
    ? "light"
    : "dark";
}

function applyTheme(resolved: ResolvedTheme): void {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.theme = resolved;
}

export function useTheme(theme: Theme | undefined): void {
  useEffect(() => {
    const choice = theme ?? "system";
    if (choice !== "system") {
      // Direct picks (light / ivory / dark / midnight) apply as-is.
      applyTheme(choice);
      return;
    }
    // System mode resolves to plain light/dark only — the variant packs
    // (ivory, midnight) are explicit picks, not OS-toggled.
    applyTheme(resolveSystem());
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return;
    }
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const handler = (e: MediaQueryListEvent) => {
      applyTheme(e.matches ? "light" : "dark");
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme]);
}
